import React, { useCallback, useState } from 'react';
import { useClarificationsContext } from '@/contexts/ClarificationsContext';
import ClarificationItem from './ClarificationItem';
import ClarificationCommentForm from './ClarificationCommentForm';
import ClarificationStatusMessage from './ClarificationStatusMessage';
import { Separator } from '@/components/ui/separator';

interface ClarificationThreadProps {
  submissionId: string | undefined;
  questionSlug: string;
}

const ClarificationThread: React.FC<ClarificationThreadProps> = ({
  submissionId,
  questionSlug,
}) => {
  const {
    getClarificationsForQuestion,
    isLoading,
    error,
    canAddClarifications,
    canResolveClarifications,
    canEditClarifications,
    canReplyOnly,
    clarificationRoundState,
    createClarification,
    resolveClarification,
    unresolveClarification,
    updateClarification,
  } = useClarificationsContext();

  const [replyingTo, setReplyingTo] = useState<string | null>(null);

  const clarifications = getClarificationsForQuestion(questionSlug);
  const isClarificationReceived = clarificationRoundState === 'responded';

  const handleCreate = useCallback(
    async (content: string) => {
      await createClarification(questionSlug, content);
    },
    [questionSlug, createClarification]
  );

  const handleReply = useCallback(
    async (content: string) => {
      if (!replyingTo) return;
      await createClarification(questionSlug, content, replyingTo);
      setReplyingTo(null);
    },
    [questionSlug, replyingTo, createClarification]
  );

  const handleResolve = useCallback(
    async (clarificationId: string) => {
      await resolveClarification(questionSlug, clarificationId);
    },
    [questionSlug, resolveClarification]
  );

  const handleUnresolve = useCallback(
    async (clarificationId: string) => {
      await unresolveClarification(questionSlug, clarificationId);
    },
    [questionSlug, unresolveClarification]
  );

  const handleUpdate = useCallback(
    async (clarificationId: string, content: string, messageId?: string) => {
      await updateClarification(clarificationId, content, messageId);
    },
    [updateClarification]
  );

  if (!submissionId) return null;

  if (isLoading && clarifications.length === 0) {
    return (
      <div className="p-4">
        <p className="text-sm text-gray-500">
          {canAddClarifications ? 'Loading clarifications...' : 'Loading...'}
        </p>
      </div>
    );
  }

  const showNewCommentForm = canAddClarifications && !canReplyOnly;

  return (
    <div className="space-y-4">
      {error && (
        <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
          {error}
        </div>
      )}

      {/* Clarification list */}
      {clarifications.length === 0 ? (
        <p className="text-sm text-gray-500">
          {canAddClarifications
            ? 'No clarifications yet.'
            : 'No items need attention yet.'}
        </p>
      ) : (
        clarifications.map(clarification => (
          <ClarificationItem
            key={clarification.id}
            clarification={clarification}
            onReply={() => setReplyingTo(clarification.id)}
            onReplySubmit={handleReply}
            onReplyCancel={() => setReplyingTo(null)}
            isReplying={replyingTo === clarification.id}
            onResolve={handleResolve}
            onUnresolve={handleUnresolve}
            onUpdate={handleUpdate}
            canAddClarifications={canAddClarifications}
            canResolveClarifications={canResolveClarifications}
            canEditClarifications={canEditClarifications}
            canReplyOnly={canReplyOnly}
          />
        ))
      )}

      {/* New comment form */}
      {showNewCommentForm && (
        <>
          <Separator className="-mx-6" />
          <ClarificationCommentForm
            onSubmit={handleCreate}
            submitLabel="Add Clarification"
            placeholder="Add a clarification..."
          />
        </>
      )}

      {/* Status / permission message */}
      <ClarificationStatusMessage
        canAddClarifications={canAddClarifications}
        canReplyOnly={canReplyOnly}
        isClarificationReceived={isClarificationReceived}
        hasClarifications={clarifications.length > 0}
      />
    </div>
  );
};

export default ClarificationThread;
